import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, X, Eye, Lock, FileWarning, ExternalLink } from "lucide-react";
import { GIVESENDGO_URL, TRAILER_EMBED, TRAILER_URL } from "../lib/api";

const HERO_IMG =
    "https://images.unsplash.com/photo-1593185595428-fb79aae0fa5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxjYXBpdG9sJTIwYnVpbGRpbmclMjBuaWdodCUyMGNpbmVtYXRpY3xlbnwwfHx8fDE3NzgxODI3ODR8MA&ixlib=rb-4.1.0&q=85";
const SURVEILLANCE_IMG =
    "https://images.unsplash.com/photo-1593347894757-3b12ab5e6247?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBkYXJrfGVufDB8fHx8MTc3ODE4Mjc3N3ww&ixlib=rb-4.1.0&q=85";
const REDACTED_IMG =
    "https://images.unsplash.com/photo-1731074803855-cdd369adc621?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxyZWRhY3RlZCUyMGRvY3VtZW50fGVufDB8fHx8MTc3ODE4Mjc4NHww&ixlib=rb-4.1.0&q=85";

const themes = [
    {
        icon: Eye,
        title: "Surveillance",
        text: "Warrantless wiretaps, deep-state spying, and snitch lines turned on ordinary citizens.",
    },
    {
        icon: Lock,
        title: "Censorship",
        text: "Speech codes, shuttered churches, and an approved narrative enforced by the experts.",
    },
    {
        icon: FileWarning,
        title: "Conspiracy",
        text: "Lab leaks, orchestrated panic, paid protests — and what happens when none of it is an accident.",
    },
];

export default function Home() {
    const [showTrailer, setShowTrailer] = useState(false);

    useEffect(() => {
        if (!showTrailer) return;
        const onKey = (e) => e.key === "Escape" && setShowTrailer(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showTrailer]);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative min-h-screen flex items-end overflow-hidden pt-32 pb-20"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Capitol at night"
                        className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/30 to-transparent" />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-3 mb-8">
                            <span className="classified-stamp text-sm sm:text-base">
                                Classified // Restricted Access
                            </span>
                        </div>

                        <h1
                            data-testid="hero-title"
                            className="font-display font-light text-6xl sm:text-8xl lg:text-9xl leading-[0.95] tracking-tighter uppercase text-balance"
                        >
                            State of
                            <br />
                            <span className="italic font-medium text-blood">
                                Deception
                            </span>
                        </h1>

                        <p className="font-accent uppercase tracking-[0.3em] text-blood text-lg sm:text-xl mt-8">
                            But what if none of it was an accident?
                        </p>

                        <p
                            data-testid="hero-tagline"
                            className="font-mono text-xl sm:text-2xl text-neutral-700 mt-6 max-w-2xl leading-relaxed"
                        >
                            A post-COVID political thriller about ‘deep state’ spying,
                            secret lab leaks, orchestrated panic — and one
                            newly-elected Senator&apos;s race to expose the truth before it
                            gets <span className="line-through text-blood">canceled</span>.
                        </p>

                        <div className="mt-12 flex flex-col sm:flex-row gap-4">
                            <button
                                data-testid="hero-watch-trailer"
                                onClick={() => setShowTrailer(true)}
                                className="group inline-flex items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-8 hover:bg-bloodHover transition-all duration-300 border border-blood"
                            >
                                <Play size={16} className="fill-white" />
                                Watch the Trailer
                            </button>
                            <a
                                href={GIVESENDGO_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-fund-btn"
                                className="group inline-flex items-center justify-center gap-3 bg-transparent text-neutral-900 font-accent uppercase tracking-widest text-lg py-4 px-8 hover:bg-black/5 transition-all duration-300 border border-neutral-400 hover:border-blood"
                            >
                                Fund the Film
                                <ArrowRight
                                    size={16}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-4 hidden lg:block">
                        <div className="border-l-2 border-blood pl-6 space-y-4">
                            <p className="font-mono text-base uppercase tracking-[0.25em] text-neutral-500">
                                File / 2026 / Lewis
                            </p>
                            <p className="font-display text-4xl text-neutral-700 italic leading-snug">
                                “History belongs to those who write it. But it also belongs to those who tell it.”
                            </p>
                            <p className="font-mono text-base uppercase tracking-[0.25em] text-neutral-500">
                                — Jason Lewis, Writer / Creator
                            </p>
                        </div>
                    </div>
                </div>

            </section>

            {/* LOGLINE */}
            <section
                data-testid="logline-section"
                className="relative py-24 sm:py-32 border-t border-neutral-200"
            >
                <div className="max-w-5xl mx-auto px-6 sm:px-8">
                    <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-8">
                        / The Logline
                    </p>
                    <p className="font-display text-3xl sm:text-4xl lg:text-5xl text-neutral-900 leading-tight tracking-tight text-balance">
                        In a Capitol ruled by corrupt media and money, a newly-elected
                        Midwestern Senator stumbles upon a vast conspiracy and starts to
                        connect the dots between a nation in turmoil and a globalist
                        cabal gone <em className="text-blood">‘viral’</em> in more ways
                        than one. Soon he&apos;s in a life-or-death struggle to save the
                        country — and himself.
                    </p>
                </div>
            </section>

            {/* THEMES */}
            <section
                data-testid="themes-section"
                className="relative py-24 sm:py-32 border-t border-neutral-200 bg-ink2"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16 items-end">
                        <div className="md:col-span-2">
                            <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-4">
                                / Dossier
                            </p>
                            <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase tracking-tight text-neutral-900 text-balance">
                                Three threads. One conspiracy.
                            </h2>
                        </div>
                        <p className="font-mono text-lg text-neutral-600 leading-relaxed">
                            From speech codes to snitch lines. From paid protests to
                            lockdowns. From warrantless wiretaps to mask mandates.
                            Welcome to the file.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-300">
                        {themes.map((t) => (
                            <div
                                key={t.title}
                                data-testid={`theme-${t.title.toLowerCase()}`}
                                className="group bg-ink p-10 hover:bg-ink3 transition-colors duration-500"
                            >
                                <t.icon
                                    size={32}
                                    className="text-blood group-hover:scale-110 transition-transform"
                                />
                                <h3 className="font-accent uppercase tracking-[0.2em] text-3xl text-neutral-900 mt-8">
                                    {t.title}
                                </h3>
                                <div className="w-12 h-px bg-blood my-4" />
                                <p className="font-mono text-lg text-neutral-600 leading-relaxed">
                                    {t.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SPLIT CTA */}
            <section
                data-testid="split-cta"
                className="relative py-24 sm:py-32 border-t border-neutral-200"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                            src={SURVEILLANCE_IMG}
                            alt="Surveillance"
                            className="w-full h-full object-cover grayscale opacity-80 hover:opacity-100 hover:grayscale-0 transition-all duration-700"
                        />
                        <div className="absolute top-4 left-4 font-mono text-sm uppercase tracking-[0.3em] text-blood bg-[#ECE8DF]/85 px-3 py-1 border border-blood">
                            Behind the Story
                        </div>
                    </div>
                    <div>
                        <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-4">
                            / Why this film
                        </p>
                        <h2 className="font-display text-6xl sm:text-7xl uppercase tracking-tight text-neutral-900 leading-[1.05] text-balance">
                            Independent films that challenge powerful institutions are rarely funded.
                        </h2>
                        <p className="font-mono text-lg sm:text-xl text-neutral-600 mt-8 leading-relaxed">
                            They are diluted, softened, or never made at all. Crowdfunding
                            allows us to protect the integrity of this story, retain creative
                            independence, and build the film alongside people who believe
                            telling the truth about this troubled time matters.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/about"
                                data-testid="cta-read-more"
                                className="inline-flex items-center justify-center gap-3 bg-transparent text-neutral-900 font-accent uppercase tracking-widest text-lg py-4 px-8 hover:bg-black/5 transition-colors border border-neutral-400 hover:border-neutral-900"
                            >
                                Read the Brief <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/get-involved"
                                data-testid="cta-get-involved"
                                className="inline-flex items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-8 hover:bg-bloodHover transition-colors border border-blood"
                            >
                                Get Involved
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FINAL CTA STRIP */}
            <section
                data-testid="final-cta"
                className="relative py-24 border-t border-neutral-200 overflow-hidden"
                style={{
                    backgroundImage: `linear-gradient(rgba(236,232,223,0.93), rgba(236,232,223,0.97)), url(${REDACTED_IMG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
                    <p className="font-accent uppercase tracking-[0.3em] text-blood text-base mb-6">
                        / Take a side
                    </p>
                    <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl text-neutral-900 uppercase leading-tight tracking-tight text-balance">
                        Stories shape the culture
                        <br />
                        <em className="text-blood">long before laws do.</em>
                    </h2>
                    <p className="font-mono text-lg text-neutral-600 mt-8 max-w-2xl mx-auto leading-relaxed">
                        Help bring State of Deception to the screen. Become a backer, audition for a non-paid role, or share the file.
                    </p>
                    <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href={GIVESENDGO_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="final-fund-btn"
                            className="inline-flex items-center justify-center gap-3 bg-blood text-white font-accent uppercase tracking-widest text-lg py-4 px-10 hover:bg-bloodHover transition-colors border border-blood"
                        >
                            Donate via GiveSendGo <ArrowRight size={16} />
                        </a>
                        <Link
                            to="/get-involved#casting"
                            data-testid="final-casting-btn"
                            className="inline-flex items-center justify-center gap-3 bg-transparent text-neutral-900 font-accent uppercase tracking-widest text-lg py-4 px-10 hover:bg-black/5 transition-colors border border-neutral-400 hover:border-neutral-900"
                        >
                            Audition for a Role
                        </Link>
                    </div>
                </div>
            </section>

            {/* TRAILER MODAL */}
            {showTrailer && (
                <div
                    data-testid="trailer-modal"
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-up"
                    onClick={() => setShowTrailer(false)}
                >
                    <button
                        data-testid="trailer-close"
                        className="absolute top-6 right-6 text-white hover:text-blood transition-colors p-2"
                        onClick={() => setShowTrailer(false)}
                        aria-label="Close trailer"
                    >
                        <X size={32} />
                    </button>
                    <div
                        className="relative w-full max-w-5xl aspect-video"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            data-testid="trailer-iframe"
                            src={`${TRAILER_EMBED}?autoplay=1`}
                            title="State of Deception — Proof of Concept Trailer"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full border border-blood"
                        />
                        <a
                            href={TRAILER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="trailer-youtube-fallback"
                            className="absolute -bottom-12 left-0 inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-neutral-400 hover:text-blood transition-colors"
                        >
                            <ExternalLink size={14} />
                            Trouble playing? Watch on YouTube
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}
